import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { generateAddress, formatDate, formatTime, formatDateTime } from "../../utils/Utilitats";
import "./seleccionaragenda.css";

function FilaAgenda({ isSel, rowRef, className, onClick, onDoubleClick, children }) {
  return (
    <tr
      ref={isSel ? rowRef : null}
      className={className}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {children}
    </tr>
  );
}

function SeleccionarAgenda({
  onSelect,
  selectedItem,
  onDoubleConsult,
  onConsultar,
  onModificar,
  onEsborrar,
  onItemsChange,
  reloadToken = 0,
  filters,
}) {
  const { empresaSeleccionada, usuariActiu, rolUsuari } = useUserContext();
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);

  const [filterTextLocal, setFilterTextLocal] = useState("");
  const [estatFilterLocal, setEstatFilterLocal] = useState("pendents_encurs");
  const [mostraAvisosLocal, setMostraAvisosLocal] = useState(true);
  const [mostraTasquesLocal, setMostraTasquesLocal] = useState(true);
  const [relacioLocal, setRelacioLocal] = useState("assignades");
  const [selectedUsuariLocal, setSelectedUsuariLocal] = useState(usuariActiu ?? null);

  const filterText = filters?.filterText ?? filterTextLocal;
  const setFilterText = filters?.setFilterText ?? setFilterTextLocal;
  const estatFilter = filters?.estatFilter ?? estatFilterLocal;
  const setEstatFilter = filters?.setEstatFilter ?? setEstatFilterLocal;
  const mostraAvisos = filters?.mostraAvisos ?? mostraAvisosLocal;
  const setMostraAvisos = filters?.setMostraAvisos ?? setMostraAvisosLocal;
  const mostraTasques = filters?.mostraTasques ?? mostraTasquesLocal;
  const setMostraTasques = filters?.setMostraTasques ?? setMostraTasquesLocal;
  const relacio = filters?.relacio ?? relacioLocal;
  const setRelacio = filters?.setRelacio ?? setRelacioLocal;
  const selectedUsuari = filters?.selectedUsuari ?? selectedUsuariLocal;
  const setSelectedUsuari = filters?.setSelectedUsuari ?? setSelectedUsuariLocal;

  const viewMode = mostraAvisos && mostraTasques ? "Resum" : mostraAvisos ? "Avisos" : "Tasques";

  const [userOptions, setUserOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "DataCreacio", direction: "desc" });
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  const selectedRowRef = useRef(null);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortAgenda = (rows, { key, direction }) => {
    if (!key) return rows;

    const getVal = (it) => {
      switch (key) {
        case "DataCreacio":
        case "DataInici":
        case "DataFinal":
          return it[key] ? new Date(it[key]).getTime() : -Infinity;
        case "Prioritat":
          return Number(it.Prioritat ?? -1);
        case "Adreca": {
          const a = {
            TipusVia: it.TipusVia,
            NomVia: it.NomVia,
            NumVia: it.NumVia || it.Numero,
            EscalaVia: it.EscalaVia || it.Escala,
            Pis: it.Pis,
            Porta: it.Porta,
          };
          return generateAddress(a) || "";
        }
        default:
          return (it[key] ?? "").toString();
      }
    };

    return [...rows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "string" || typeof vb === "string") {
        const res = collator.compare(va, vb);
        return direction === "asc" ? res : -res;
      }
      const res = va < vb ? -1 : va > vb ? 1 : 0;
      return direction === "asc" ? res : -res;
    });
  };

  const formatPrioritat = (p) => ({ 3: "Màxima", 2: "Alta", 1: "Mitja", 0: "Baixa" }[p] ?? "—");

  const rowTipusClass = (it) => (it.Tipus === "Tasca" ? "Agenda-fila-tasca" : "Agenda-fila-avis");

  const TipusBadge = ({ tipus }) => (
    <span className={`Agenda-badge ${tipus === "Tasca" ? "Agenda-badge-tasca" : "Agenda-badge-avis"}`}>
      {tipus === "Tasca" ? "Tasca" : "Avís"}
    </span>
  );

  const EstatBadge = ({ estat }) => {
    const key = (estat || "").toLowerCase();
    return <span className={`Agenda-estat-badge Agenda-estat-${key || "desconegut"}`}>{estat || "—"}</span>;
  };

  const addressFromItem = (it) => {
    const a = {
      TipusVia: it.TipusVia,
      NomVia: it.NomVia,
      NumVia: it.NumVia || it.Numero,
      EscalaVia: it.EscalaVia || it.Escala,
      Pis: it.Pis,
      Porta: it.Porta,
    };
    const addr = generateAddress(a);
    return { addr: addr || "—", pob: it.Poblacio || "—" };
  };

  useEffect(() => {
    const fetchUsuaris = async () => {
      if (!empresaSeleccionada?.IdEmpresa) {
        setUserOptions([]);
        return;
      }
      try {
        const resp = await fetchAutenticat(`${API_URL}/AgendaUsuaris.php`, {
          method: "POST",
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            ambit: "safata",
          }),
        });
        const data = await resp.json();
        const arr = Array.isArray(data?.usuaris) ? data.usuaris : [];
        const norm = arr
          .map((u) => ({
            idUsuaris: u.idUsuaris ?? u.idUsuari ?? u.Id ?? null,
            NomUsuari: u.NomUsuari ?? u.Nom ?? "",
          }))
          .filter((u) => u.idUsuaris != null);
        setUserOptions(norm);
      } catch (e) {
        if (e instanceof SessioCaducadaError) return;
        setUserOptions([]);
      }
    };
    fetchUsuaris();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchAgenda = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      if (selectedUsuari == null) return;
      setLoading(true);
      try {
        const resp = await fetchAutenticat(`${API_URL}/Agenda.php`, {
          method: "POST",
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            avisosUsuari: selectedUsuari,
            relacio,
            estat: estatFilter,
          }),
        });
        const data = await resp.json();
        const arr = Array.isArray(data?.agenda) ? data.agenda : [];
        setItems(arr);
        setFilteredItems(arr);
      } catch (e) {
        if (e instanceof SessioCaducadaError) return;
        setItems([]);
        setFilteredItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAgenda();
  }, [empresaSeleccionada, selectedUsuari, relacio, estatFilter, reloadToken]);

  useEffect(() => {
    const text = (filterText || "").toLowerCase().trim();
    const filtered = items.filter((it) => {
      const matchMode = (mostraAvisos && it.Tipus === "Avis") || (mostraTasques && it.Tipus === "Tasca");
      const { addr, pob } = addressFromItem(it);
      const haystack = [
        it.Titol,
        it.Descripcio,
        it.Tipus,
        it.Estat,
        it.NomEntitat,
        it.NomContacte,
        it.TelefonMostrat,
        it.EmailMostrat,
        addr,
        pob,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchMode && (!text || haystack.includes(text));
    });
    setFilteredItems(filtered);
  }, [filterText, mostraAvisos, mostraTasques, items]);

  const sortedItems = sortAgenda(filteredItems, sortConfig);
  const lastSigRef = React.useRef("");

  useEffect(() => {
    if (!onItemsChange) return;
    const sig = sortedItems.map((x) => x.idAgenda).join(",");
    if (sig !== lastSigRef.current) {
      onItemsChange(sortedItems);
      lastSigRef.current = sig;
    }
  }, [sortedItems, onItemsChange]);

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedItem, sortedItems]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const toggleAvisos = () => {
    if (mostraAvisos && !mostraTasques) return;
    setMostraAvisos(!mostraAvisos);
  };
  const toggleTasques = () => {
    if (mostraTasques && !mostraAvisos) return;
    setMostraTasques(!mostraTasques);
  };

  const nomUsuariSafata =
    selectedUsuari && selectedUsuari !== 0
      ? userOptions.find((u) => Number(u.idUsuaris) === Number(selectedUsuari))?.NomUsuari
      : null;
  const veientAltre =
    selectedUsuari && selectedUsuari !== 0 && Number(selectedUsuari) !== Number(usuariActiu);

  const obrirMenu = (e, it) => {
    e.stopPropagation();
    const id = it.idAgenda;
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const itemMenu = menuObert ? sortedItems.find((it) => it.idAgenda === menuObert) : null;
  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  const th = (key, label, extraClass) => (
    <th
      className={`${extraClass || ""} ${sortConfig.key === key ? "is-sorted" : ""}`.trim()}
      onClick={() => handleSort(key)}
    >
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (it) => (
    <div className="dv2-table-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === it.idAgenda ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, it)}
      >
        …
      </button>
    </div>
  );

  const filaProps = (it) => {
    const isSel = selectedItem && selectedItem.idAgenda === it.idAgenda;
    return {
      isSel,
      rowRef: selectedRowRef,
      className: `${rowTipusClass(it)} ${isSel ? "is-selected" : ""}`,
      onClick: () => onSelect?.(it),
      onDoubleClick: () => {
        onSelect?.(it);
        if (!pucConsultar) return;
        (onConsultar || onDoubleConsult)?.(it);
      },
    };
  };

  const renderFiles = () =>
    sortedItems.map((it) => {
      const { addr } = addressFromItem(it);
      if (viewMode === "Avisos") {
        return (
          <FilaAgenda key={it.idAgenda} {...filaProps(it)}>
            <td className="col-datetime">{formatDateTime(it.DataCreacio) || "—"}</td>
            <td>{it.NomEntitat || "—"}</td>
            <td>{it.NomContacte || "—"}</td>
            <td>{addr || "—"}</td>
            <td>{it.TelefonMostrat || "—"}</td>
            <td>{it.EmailMostrat || "—"}</td>
            <td>{it.Titol || "—"}</td>
            <td>
              <EstatBadge estat={it.Estat} />
            </td>
            <td>{formatPrioritat(it.Prioritat)}</td>
            <td>{it.NomCreatPer || "—"}</td>
            <td className="dv2-table-actions">{renderAccions(it)}</td>
          </FilaAgenda>
        );
      }
      if (viewMode === "Tasques") {
        return (
          <FilaAgenda key={it.idAgenda} {...filaProps(it)}>
            <td className="col-datetime">{formatDateTime(it.DataCreacio) || "—"}</td>
            <td className="col-datetime">
              {it.DataInici ? `${formatDate(it.DataInici)} ${formatTime(it.DataInici)}` : "—"}
            </td>
            <td className="col-datetime">
              {it.DataFinal ? `${formatDate(it.DataFinal)} ${formatTime(it.DataFinal)}` : "—"}
            </td>
            <td>{it.NomEntitat || "—"}</td>
            <td>{it.NomContacte || "—"}</td>
            <td>{it.TelefonMostrat || "—"}</td>
            <td>{it.Titol || "—"}</td>
            <td>
              <EstatBadge estat={it.Estat} />
            </td>
            <td>{formatPrioritat(it.Prioritat)}</td>
            <td>{it.NomCreatPer || "—"}</td>
            <td className="dv2-table-actions">{renderAccions(it)}</td>
          </FilaAgenda>
        );
      }
      return (
        <FilaAgenda key={it.idAgenda} {...filaProps(it)}>
          <td>
            <TipusBadge tipus={it.Tipus} />
          </td>
          <td className="col-datetime">{formatDateTime(it.DataCreacio) || "—"}</td>
          <td>{it.NomEntitat || "—"}</td>
          <td>{it.NomContacte || "—"}</td>
          <td>{it.TelefonMostrat || "—"}</td>
          <td>{it.Titol || "—"}</td>
          <td>
            <EstatBadge estat={it.Estat} />
          </td>
          <td>{formatPrioritat(it.Prioritat)}</td>
          <td>{it.NomCreatPer || "—"}</td>
          <td className="dv2-table-actions">{renderAccions(it)}</td>
        </FilaAgenda>
      );
    });

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per títol, contacte, telèfon, adreça…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Estat</span>
          <select className="dv2-select" value={estatFilter} onChange={(e) => setEstatFilter(e.target.value)}>
            <option value="pendents_encurs">Pendents + En curs</option>
            <option value="Pendent">Pendents</option>
            <option value="EnCurs">En curs</option>
            <option value="Finalitzat">Finalitzats</option>
            <option value="Rebutjat">Rebutjats</option>
            <option value="tots">Tots</option>
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Usuari</span>
          <select
            className="dv2-select"
            value={selectedUsuari ?? ""}
            onChange={(e) => setSelectedUsuari(Number(e.target.value))}
          >
            <option value={0}>Tots</option>
            {userOptions.map((u) => (
              <option key={u.idUsuaris} value={u.idUsuaris}>
                {u.NomUsuari}
                {usuariActiu && Number(u.idUsuaris) === Number(usuariActiu) ? " (jo)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Relació</span>
          <select className="dv2-select" value={relacio} onChange={(e) => setRelacio(e.target.value)}>
            <option value="assignades">Assignades</option>
            <option value="creades">Creades</option>
            <option value="participades">Participades</option>
            <option value="traspassades">Traspassades</option>
            <option value="tots">Tots</option>
          </select>
        </label>
      </div>

      {veientAltre && nomUsuariSafata && <p className="Agenda-safata-de">Agenda de: {nomUsuariSafata}</p>}

      <div className="Agenda-resum-comptadors">
        <button
          type="button"
          className={`Agenda-badge Agenda-badge-avis ${mostraAvisos ? "" : "Agenda-badge-inactiu"}`}
          onClick={toggleAvisos}
        >
          {mostraAvisos ? "✓ " : ""}
          {items.filter((it) => it.Tipus === "Avis").length} avisos
        </button>
        <button
          type="button"
          className={`Agenda-badge Agenda-badge-tasca ${mostraTasques ? "" : "Agenda-badge-inactiu"}`}
          onClick={toggleTasques}
        >
          {mostraTasques ? "✓ " : ""}
          {items.filter((it) => it.Tipus === "Tasca").length} tasques
        </button>
      </div>

      {loading ? (
        <p className="dv2-hint">Carregant agenda…</p>
      ) : sortedItems.length > 0 ? (
        isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {sortedItems.map((it) => {
              const isSel = selectedItem && selectedItem.idAgenda === it.idAgenda;
              return (
                <article
                  key={it.idAgenda}
                  className={`dv2-record ${rowTipusClass(it)} ${isSel ? "is-selected" : ""}`}
                  onClick={() => onSelect?.(it)}
                  onDoubleClick={() => {
                    onSelect?.(it);
                    if (pucConsultar) (onConsultar || onDoubleConsult)?.(it);
                  }}
                >
                  <div>
                    <p className="dv2-record-title">{it.Titol || "—"}</p>
                    <p className="dv2-record-meta">
                      {[it.Tipus === "Tasca" ? "Tasca" : "Avís", it.Estat, it.NomContacte || it.NomEntitat]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {renderAccions(it)}
                </article>
              );
            })}
          </div>
        ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            {viewMode === "Avisos" ? (
              <thead>
                <tr>
                  {th("DataCreacio", "Alta", "col-datetime")}
                  {th("NomEntitat", "Entitat")}
                  {th("NomContacte", "Contacte")}
                  {th("Adreca", "Adreça")}
                  {th("TelefonMostrat", "Telèfon")}
                  {th("EmailMostrat", "Email")}
                  {th("Titol", "Títol")}
                  {th("Estat", "Estat")}
                  {th("Prioritat", "Prioritat")}
                  {th("NomCreatPer", "Creat")}
                  <th className="dv2-table-actions"></th>
                </tr>
              </thead>
            ) : viewMode === "Tasques" ? (
              <thead>
                <tr>
                  {th("DataCreacio", "Alta", "col-datetime")}
                  {th("DataInici", "Data Inici", "col-datetime")}
                  {th("DataFinal", "Data Final", "col-datetime")}
                  {th("NomEntitat", "Entitat")}
                  {th("NomContacte", "Contacte")}
                  {th("TelefonMostrat", "Telèfon")}
                  {th("Titol", "Títol")}
                  {th("Estat", "Estat")}
                  {th("Prioritat", "Prioritat")}
                  {th("NomCreatPer", "Creat")}
                  <th className="dv2-table-actions"></th>
                </tr>
              </thead>
            ) : (
              <thead>
                <tr>
                  {th("Tipus", "Tipus")}
                  {th("DataCreacio", "Alta", "col-datetime")}
                  {th("NomEntitat", "Entitat")}
                  {th("NomContacte", "Contacte")}
                  {th("TelefonMostrat", "Telèfon")}
                  {th("Titol", "Títol")}
                  {th("Estat", "Estat")}
                  {th("Prioritat", "Prioritat")}
                  {th("NomCreatPer", "Creat")}
                  <th className="dv2-table-actions"></th>
                </tr>
              </thead>
            )}
            <tbody>{renderFiles()}</tbody>
          </table>
        </div>
        )
      ) : (
        <p className="dv2-empty">No s'han trobat registres.</p>
      )}

      {itemMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onConsultar?.(itemMenu))}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onModificar?.(itemMenu))}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => tancarI(() => onEsborrar?.(itemMenu))}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarAgenda;
